import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { ContextService } from '../services/context.service';
import { LogContext } from '../models/log-context.interface';

/**
 * KinectiveHeadersInterceptor
 * 
 * Automatically adds Kinective metadata from the logging system to all outgoing HTTP requests as headers.
 * This enables end-to-end tracing, correlation, and context-aware processing across distributed systems.
 * 
 * The interceptor retrieves the current LogContext from ContextService and maps it to HTTP headers
 * following the X-Kinective-* naming convention.
 */
@Injectable()
export class KinectiveHeadersInterceptor implements HttpInterceptor {
  
  constructor(private contextService: ContextService) {}

  /**
   * Intercepts HTTP requests and adds Kinective metadata headers
   * 
   * @param request The outgoing HTTP request
   * @param next The next handler in the interceptor chain
   * @returns Observable of the HTTP event
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    try {
      // Retrieve current context from ContextService
      const context = this.contextService.getContext();

      // If context is unavailable, pass request through unchanged
      if (!context) {
        console.warn('KinectiveHeadersInterceptor: Context is null or undefined');
        return next.handle(request);
      }

      // Generate correlation ID for this request
      const correlationId = this.contextService.generateCorrelationId();
      
      // Add correlation ID to context for this request
      const contextWithCorrelationId = {
        ...context,
        correlationId
      };

      // Add Kinective headers to the request
      const modifiedRequest = this.addKinectiveHeaders(request, contextWithCorrelationId);
      return next.handle(modifiedRequest);

    } catch (error) {
      // Handle errors gracefully - never block requests
      console.error('KinectiveHeadersInterceptor: Failed to add headers', error);
      return next.handle(request);
    }
  }

  /**
   * Helper method that adds Kinective headers based on available context fields
   * 
   * Only adds headers for non-null, non-undefined, non-empty string values.
   * Clones the request to ensure immutability.
   * 
   * @param request The original HTTP request
   * @param context The current LogContext with Kinective metadata
   * @returns A cloned request with Kinective headers added
   */
  private addKinectiveHeaders(
    request: HttpRequest<any>,
    context: LogContext
  ): HttpRequest<any> {
    let headers = request.headers;

    // Helper function to check if a value should be added as a header
    const isValidValue = (value: any): boolean => {
      return value !== null && value !== undefined && value !== '';
    };

    if (isValidValue(context.product)) {
      headers = headers.set('X-Kinective-Product', context.product!);
    }

    if (isValidValue(context.environment)) {
      headers = headers.set('X-Kinective-Environment', context.environment!);
    }

    if (isValidValue(context.service)) {
      headers = headers.set('X-Kinective-Service', context.service!);
    }

    if (isValidValue(context.correlationId)) {
      headers = headers.set('X-Correlation-Id', context.correlationId!);
    }

    if (isValidValue(context.sessionId)) {
      headers = headers.set('X-Session-Id', context.sessionId!);
    }

    if (isValidValue(context.partner)) {
      headers = headers.set('X-Kinective-Partner-Name', context.partner!);
    }

    if (isValidValue(context.partnerId)) {
      headers = headers.set('X-Kinective-Partner-Id', context.partnerId!);
    }

    if (isValidValue(context.institution)) {
      headers = headers.set('X-Kinective-Institution-Name', context.institution!);
    }

    if (isValidValue(context.assetId)) {
      headers = headers.set('X-Kinective-Institution-Id', context.assetId!);
    }

    if (isValidValue(context.externalId)) {
      headers = headers.set('X-Kinective-External-Id', context.externalId!);
    }

    // Clone the request with updated headers
    return request.clone({ headers });
  }
}